import { compare } from 'owa-datetime';
import { EventEntity } from '../schema/EventEntity';
import { EventsCache, EventsCacheLockId } from '../schema/EventsCache';
import { findInsertIndex } from '../utils/findInsertIndex';
import { trace } from 'owa-trace';
import { tryInitializeLockInfo } from './tryInitializeLockInfo';
import { updateEventProperties } from './updateEventProperties';
import { upsertEvents } from './upsertEvents';

const startTimeComparePredicate = <T extends EventEntity>(event1: T, event2: T) =>
    compare(event1.Start, event2.Start);

export function addEventForLock<T extends EventEntity>(
    event: T,
    lockId: EventsCacheLockId,
    cache: EventsCache<T>,
    hasEventChanged: boolean = false,
    forceOverrideAllPropertiesForEventIfAvailable: boolean = false
): T {
    tryInitializeLockInfo(lockId, cache);

    const { events } = cache;

    if (hasEventChanged) {
        const existingEventInfo = getExistingEventWithKey(event.Key, events);

        if (existingEventInfo.event) {
            // If we found an existing event with the same key
            // then we delete that from the list and also re add
            // exactly the same object with the new properties assigned.
            // This is done so that anyone having the reference of the existing event
            // would not be invalidated. The reason to delete it is so that it can be
            // added in the list in correct order as the time can change if an event is
            // added again (e.g. in case of fetching series master).
            events.splice(existingEventInfo.index, 1);
            updateEventProperties(
                existingEventInfo.event,
                event,
                forceOverrideAllPropertiesForEventIfAvailable
            );
            event = existingEventInfo.event;
        }
    }

    const index: number = findInsertIndex(event, events, startTimeComparePredicate);

    const eventsStartIndex = index >= 0 ? index : 0;
    trace.info(
        `[owa-events-cache] Adding the event by start looking from index ${eventsStartIndex}`
    );

    const [eventAdded] = upsertEvents(
        [event],
        events,
        eventsStartIndex,
        forceOverrideAllPropertiesForEventIfAvailable
    );

    // Adding the event to the locked events list
    const { lockedEvents } = cache.locksInfo.get(lockId);
    lockedEvents[event.Key] = true;

    return eventAdded;
}

function getExistingEventWithKey<T extends EventEntity>(
    key: string,
    events: T[]
): { event: T; index: number } | { event: null; index: -1 } {
    let existingEvent: T = null;
    let index = 0;
    for (; index < events.length; index++) {
        if (events[index].Key === key) {
            existingEvent = events[index];
            break;
        }
    }
    return existingEvent ? { event: existingEvent, index } : { event: null, index: -1 };
}
